"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Cpu, Microscope, Wrench, Layers, ChevronRight, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const workflowSteps = [{
  step: "01",
  phase: "Challenges",
  title: "Data Integrity & Regulatory Audits",
  desc: "Addressing compliance requirements and data integrity standards.",
  badge: "Risk Point",
  iconName: "alert"
}, {
  step: "02",
  phase: "Technologies",
  title: "Advanced Spectrometry & HPLC",
  desc: "Deploying high-precision chromatography and automated analyzers.",
  badge: "Hardware",
  iconName: "cpu"
}, {
  step: "03",
  phase: "Applications",
  title: "Batch Release & In-Process QC",
  desc: "Optimized workflows for sterile fill-finish and bioprocessing labs.",
  badge: "Deployment",
  iconName: "microscope"
}, {
  step: "04",
  phase: "Services",
  title: "IQ/OQ/PQ Lifecycle Validation",
  desc: "Comprehensive protocol authoring, execution, and requalification.",
  badge: "Compliance",
  iconName: "file"
}, {
  step: "05",
  phase: "Products",
  title: "Instrumentation & Consumables Support",
  desc: "Factory-calibrated detectors, standards, and audited consumables.",
  badge: "Supply",
  iconName: "layers"
}, {
  step: "06",
  phase: "Next Steps",
  title: "Facility Site Audit & Readiness Plan",
  desc: "Roadmap formulation and seamless tech transfer consultation.",
  badge: "Outcome",
  iconName: "check"
}];
const complianceMetrics = [{
  label: "Support Model",
  value: "Application-Led",
  sub: "Dedicated technical specialists"
}, {
  label: "Sectors Covered",
  value: "Industries Served",
  sub: "Regulated & laboratory facilities"
}, {
  label: "Coverage Region",
  value: "Ireland & UK",
  sub: "Dedicated on-site field team"
}];
export default function ArchitectureHero() {
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "alert":
        return <AlertCircle className="size-4 text-primary" />;
      case "cpu":
        return <Cpu className="size-4 text-primary" />;
      case "microscope":
        return <Microscope className="size-4 text-primary" />;
      case "file":
        return <FileCheck2 className="size-4 text-primary" />;
      case "layers":
        return <Layers className="size-4 text-primary" />;
      case "check":
        return <CheckCircle2 className="size-4 text-primary" />;
      default:
        return <ShieldCheck className="size-4 text-primary" />;
    }
  };
  return <section id="herocompliancearchitecture" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Navy-tinted subtle ambient background pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(15,37,55,0.08),rgba(255,255,255,0))]" />
      <div aria-hidden="true" className="pointer-events-none absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/60 blur-3xl" />

      <div className="page-container">
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="inline-flex items-center text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center">
              <span aria-current="page" className="font-medium text-foreground">
                Industries
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Left Column: Heading, Value Proposition, Dual CTAs & Key Stats */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="flex flex-col lg:col-span-6 lg:pt-4">
            <div className="mb-4 flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>{"Industries We Support"}</span>
              </Badge>
              <span className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                {"Ireland & UK Support"}
              </span>
            </div>

            <h1 className="text-balance font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.15]">
              {"Analytical solutions for regulated manufacturing, research and public safety"}
            </h1>

            <p className="mt-6 text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
              {"Antech supports pharmaceutical, biopharmaceutical, chemical, research and public-safety organisations with analytical instrumentation, application development, qualification and lifecycle support across Ireland and the UK."}
            </p>

            {/* Dual CTAs */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button size="lg" asChild className="group shadow-md">
                <Link to="/DiscussYourApplicationPage">
                  <span>{"Discuss Your Application"}</span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="#industriesgrid">
                  <span>{"View Industries"}</span>
                </Link>
              </Button>
            </div>

            {/* Key Trust Metrics */}
            <div className="mt-12 grid grid-cols-3 gap-4 border-t border-border pt-8">
              {complianceMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col">
                  <span className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                    {metric.value}
                  </span>
                  <span className="mt-1 text-xs font-semibold text-foreground">
                    {metric.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {metric.sub}
                  </span>
                </div>)}
            </div>

            {/* Laboratory Accreditation Footnote */}
            <div className="mt-8 flex items-center gap-4 rounded-lg border border-border bg-secondary/50 p-3.5">
              <div className="relative size-12 shrink-0 overflow-hidden rounded-md border border-border bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_qualification_icon.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_qualification_icon.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_qualification_icon.png-wvc-sizes" alt="Laboratory Qualification Standard" loading="lazy" decoding="async" className="size-full object-cover" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-foreground">
                  {"Qualification & Lifecycle Support"}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {"Structured qualification frameworks tailored to regulated laboratory and manufacturing facilities."}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Standard Industry Architecture Preview Panel */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.65,
          delay: 0.15,
          ease: "easeOut"
        }} className="flex flex-col lg:col-span-6">
            <Card className="border-border bg-card shadow-lg">
              {/* Architecture Panel Header */}
              <div className="border-b border-border bg-secondary/40 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-primary" />
                      <h2 className="font-default text-base font-bold text-foreground">
                        {"Standard Industry Architecture"}
                      </h2>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {"End-to-end operational blueprint for audited production and QA."}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[11px] font-semibold">
                    {"Interactive Workflow"}
                  </Badge>
                </div>
              </div>

              {/* Step Sequence Flow */}
              <CardContent className="divide-y divide-border/60 p-0">
                {workflowSteps.map((item, i) => <div key={item.step} data-index={i} className="group flex items-start gap-4 p-4 transition-colors hover:bg-secondary/30 sm:px-6">
                    {/* Step Numeric Node */}
                    <div className="flex flex-col items-center">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-secondary font-mono text-xs font-bold text-secondary-foreground shadow-2xs group-hover:border-primary/50 group-hover:text-primary">
                        {item.step}
                      </div>
                    </div>

                    {/* Step Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            {item.phase}
                          </span>
                          <span className="text-muted-foreground/40">{"•"}</span>
                          <span className="text-xs font-semibold text-foreground">
                            {item.title}
                          </span>
                        </div>
                        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
                          {item.badge}
                        </Badge>
                      </div>

                      <p className="mt-1 text-xs leading-normal text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>

                    {/* Step Action Chevron */}
                    <div className="hidden self-center sm:block">
                      <ChevronRight className="size-4 text-muted-foreground/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </div>
                  </div>)}
              </CardContent>

              {/* Panel Footer Action */}
              <div className="flex flex-col items-center justify-between gap-3 border-t border-border bg-secondary/20 px-6 py-4 sm:flex-row">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary" />
                  <span>{"Structured Qualification Approach"}</span>
                </div>
                <Button size="sm" variant="ghost" asChild className="h-8 text-xs font-semibold">
                  <Link to="/DiscussYourApplicationPage">
                    <span>{"Discuss Your Application"}</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}